import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import {
  LayoutDashboard, Store, Calendar, Users, BarChart3, LogOut, Menu, X,
  HandCoins, ClipboardList, PackageOpen, Smartphone, Shield, Activity, Trophy,
  TrendingUp
} from 'lucide-react';

const LOGO_URL = '/logo.png';

const ALL_NAV_ITEMS = [
  { section: 'Overview' },
  { path: '/owner/dashboard', icon: LayoutDashboard, label: 'Dashboard', ownerOnly: true },
  { path: '/owner/recovery', icon: HandCoins, label: 'Loan Dashboard' },
  { section: 'Operations', ownerOnly: true },
  { path: '/owner/merchants', icon: Store, label: 'Merchants', ownerOnly: true },
  { path: '/owner/followups', icon: Calendar, label: 'Follow-ups', ownerOnly: true },
  { section: 'Team Insights' },
  { path: '/my-performance', icon: TrendingUp, label: 'My Stats' },
  { path: '/owner/team-360', icon: Trophy, label: 'Team 360' },
  { path: '/owner/team-activity', icon: Activity, label: 'Team Activity' },
  { section: 'Loan Portfolio' },
  { path: '/owner/recovery/cases', icon: ClipboardList, label: 'Loan Portfolio', portfolioGroup: true },
  { path: '/owner/recovery/activity', icon: Activity, label: 'Activity & Collections' },
  { section: 'Field Capture', managerOnly: true },
  { path: '/dashboard', icon: Smartphone, label: 'Field Mode', managerOnly: true },
  { section: 'Setup' },
  { path: '/owner/catalog', icon: PackageOpen, label: 'Products & Schemes', ownerOnly: true },
  { path: '/owner/team', icon: Users, label: 'Team' },
  { path: '/owner/reports', icon: BarChart3, label: 'Reports' },
];

function getNavItemsForRole(role) {
  const filtered = ALL_NAV_ITEMS.filter((it) => {
    if (role === 'manager') return !it.ownerOnly;
    if (role === 'admin') return !it.ownerOnly && !it.managerOnly;
    return !it.managerOnly;
  });
  // Drop section headers that have no items beneath them.
  const result = [];
  for (let i = 0; i < filtered.length; i++) {
    const it = filtered[i];
    if (it.section) {
      const next = filtered[i + 1];
      if (!next || next.section) continue;
    }
    result.push(it);
  }
  return result;
}

export function OwnerLayout({ children, title, overdueCount = 0 }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const user = JSON.parse(localStorage.getItem('au_user') || '{}');
  const isManager = user.role === 'manager';
  const isAdmin = user.role === 'admin';
  const navItems = getNavItemsForRole(user.role);
  const initials = user.name?.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) || (isManager ? 'MG' : isAdmin ? 'AD' : 'OW');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('au_token');
    localStorage.removeItem('au_user');
    navigate('/login');
  };

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="owner-wrap">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`owner-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sb-logo">
          <div className="sb-logo-row">
            <img src={LOGO_URL} alt="Logo" className="sb-logo-img" />
            <div className="sb-brand-name">
              <div>
                <span className="au-amravati">Amravati</span>
                <span className="au-urban"> Urban</span>
              </div>
              <div className="sb-sub">Co-Op Credit Society</div>
            </div>
          </div>
          <span className="sb-owner-tag">{isManager ? 'Manager Portal' : isAdmin ? 'Admin Portal' : 'Director Portal'}</span>
          {(isManager || isAdmin) && user.branchName && (
            <div style={{ fontSize: '11px', color: 'var(--g3)', marginTop: '6px', textAlign: 'center', fontWeight: 600 }}>
              {user.branchName}
            </div>
          )}
        </div>

        <nav className="sb-nav">
          {isAdmin && (
            <a
              className="sb-nav-item"
              onClick={() => { navigate('/admin'); setSidebarOpen(false); }}
              style={{ background: '#fef3c7', color: '#92400e', fontWeight: 600 }}
              data-testid="nav-admin-panel"
            >
              <Shield size={16} />
              Admin Panel
            </a>
          )}
          {navItems.map((item, idx) => {
            if (item.section) {
              return <div key={idx} className="sb-nav-section">{item.section}</div>;
            }
            // Portfolio group: light up for cases/loans/customers (and their sub-routes)
            const isActive = item.portfolioGroup
              ? (loc.pathname.startsWith('/owner/recovery/cases')
                  || loc.pathname.startsWith('/owner/recovery/loans')
                  || loc.pathname.startsWith('/owner/recovery/customers'))
              : (item.path === '/owner/recovery'
                  ? loc.pathname === item.path
                  : (loc.pathname === item.path || loc.pathname.startsWith(item.path + '/')));
            const showBadge = item.path === '/owner/followups' && overdueCount > 0;
            return (
              <a
                key={item.path}
                className={`sb-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={16} />
                {item.label}
                {showBadge && <span className="sb-nav-count">{overdueCount}</span>}
              </a>
            );
          })}
        </nav>

        <div className="sb-footer">
          <div className="sb-user-row">
            <div className="sb-user-av">{initials}</div>
            <div>
              <div className="sb-user-name">{user.name || (isManager ? 'Manager' : isAdmin ? 'Admin' : 'Director')}</div>
              <div className="sb-user-role">{isManager ? 'Manager' : isAdmin ? 'Admin' : 'Director'}</div>
            </div>
            <button className="sb-logout" onClick={handleLogout} data-testid="owner-logout-btn" title="Sign out">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="owner-main">
        <header className="owner-topbar">
          <button
            className="lg:hidden p-2 -ml-2 mr-2"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu size={20} />
          </button>
          <div className="owner-topbar-title">{title || 'Dashboard'}</div>
          <div className="owner-topbar-date">{today}</div>
        </header>
        <div className="owner-page">
          {children}
        </div>
      </main>
    </div>
  );
}
